/**
 * Print-checklist HTML builder for the staff Production page.
 * Pure string generation — extracted from Production.jsx.
 */

import { locationHeading, locationLabel } from '../../../lib/locationLabel';

const buildOrdersHTML = (printData) => {
  return printData.orders_by_client?.map(order => `
    <div class="order-card">
      <div class="order-header">
        <div>
          <span class="client">
            ${locationHeading(order)}
            ${order.is_custom ? '<span class="custom-badge">CUSTOM</span>' : ''}
          </span>
          ${order.delivery_address ? `<div class="address">${order.delivery_address}</div>` : ''}
          <div class="details">
            ${order.order_number ? `<span>Order: ${order.order_number}</span>` : ''}
            ${order.delivery_time ? `<span> • Delivery: ${order.delivery_time}</span>` : ''}
            ${order.is_custom ? '' : `<span> • Location: ${locationLabel(order)}</span>`}
          </div>
        </div>
        <div class="order-total">${order.total_donuts} donuts</div>
      </div>
      <div class="order-items">
        ${order.items.map(item => `
          <div class="order-item">
            <div class="checkbox"></div>
            <span class="item-name">${item.product_name}</span>
            <span class="item-qty">${item.quantity}</span>
          </div>
        `).join('')}
      </div>
    </div>
  `).join('') || '';
};

export const buildChecklistHTML = (printData) => `
  <!DOCTYPE html>
  <html>
  <head>
    <title>Production Checklist - ${printData.date}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; color: #333; }
      
      /* Header */
      .header { text-align: center; margin-bottom: 25px; border-bottom: 3px solid #000; padding-bottom: 15px; }
      .header h1 { font-size: 22px; margin-bottom: 5px; color: #000; }
      .header .date { font-size: 14px; color: #666; }
      
      /* Section Headers */
      h2 { font-size: 14px; margin: 20px 0 12px 0; border-bottom: 2px solid #333; padding-bottom: 5px; color: #000; }
      
      /* Summary Stats Grid */
      .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
      .summary-box { border: 2px solid #333; padding: 12px; text-align: center; border-radius: 4px; }
      .summary-box .number { font-size: 24px; font-weight: bold; color: #000; }
      .summary-box .label { font-size: 10px; color: #666; margin-top: 4px; }
      
      /* Category/Product Grid */
      .category-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 20px; }
      .category-box { border: 1px solid #999; padding: 10px; display: flex; justify-content: space-between; align-items: center; border-radius: 4px; background: #f9f9f9; }
      .category-box .name { text-transform: capitalize; font-weight: 600; font-size: 11px; }
      .category-box .qty { font-size: 18px; font-weight: bold; color: #000; }
      .one-off-tag { font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; color: #666; border: 1px solid #bbb; border-radius: 3px; padding: 1px 4px; margin-left: 5px; }
      
      /* Order Cards */
      .order-card { border: 2px solid #333; margin-bottom: 16px; border-radius: 6px; overflow: hidden; page-break-inside: avoid; }
      .order-header { background: #f0f0f0; padding: 12px; border-bottom: 2px solid #333; display: flex; justify-content: space-between; align-items: flex-start; }
      .order-header .client { font-weight: bold; font-size: 15px; color: #000; }
      .order-header .address { font-size: 11px; color: #333; margin-top: 3px; }
      .order-header .details { font-size: 10px; color: #555; margin-top: 4px; }
      .order-header .order-total { font-weight: bold; font-size: 16px; color: #000; }
      
      /* Custom Badge */
      .custom-badge { 
        background: #e9d5ff; 
        color: #7c3aed; 
        padding: 3px 8px; 
        font-size: 10px; 
        border-radius: 4px; 
        margin-left: 8px; 
        font-weight: bold;
        border: 1px solid #7c3aed;
      }
      
      /* Order Items */
      .order-items { padding: 8px 12px; }
      .order-item { display: flex; align-items: center; padding: 10px 0; border-bottom: 1px dotted #ccc; }
      .order-item:last-child { border-bottom: none; }
      
      /* Checkbox */
      .checkbox { 
        width: 18px; 
        height: 18px; 
        border: 2px solid #333; 
        margin-right: 12px; 
        flex-shrink: 0; 
        border-radius: 3px;
        background: #fff;
      }
      
      .item-name { flex: 1; font-size: 12px; }
      .item-qty { font-weight: bold; width: 50px; text-align: right; font-size: 14px; }
      
      /* Footer */
      .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #ddd; padding-top: 10px; }
      
      @media print {
        body { padding: 10px; }
        .order-card { break-inside: avoid; }
      }
    </style>
  </head>
  <body>
    <!-- Header -->
    <div class="header">
      <h1>MAD Donuts - Production Checklist</h1>
      <div class="date">${new Date(printData.date + 'T12:00:00').toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      })}</div>
    </div>

    <!-- Summary Stats -->
    <div class="summary-grid">
      <div class="summary-box">
        <div class="number">${printData.summary?.total_donuts || 0}</div>
        <div class="label">Total Donuts</div>
      </div>
      <div class="summary-box">
        <div class="number">${printData.summary?.total_orders || 0}</div>
        <div class="label">Total Orders</div>
      </div>
      <div class="summary-box">
        <div class="number">${printData.summary?.products?.length || 0}</div>
        <div class="label">Products</div>
      </div>
      <div class="summary-box">
        <div class="number">${printData.summary?.by_category?.length || 0}</div>
        <div class="label">Categories</div>
      </div>
    </div>

    <!-- Summary by Donut Type -->
    <h2>Summary by Donut Type</h2>
    <div class="category-grid">
      ${printData.summary?.by_category?.map(cat => `
        <div class="category-box">
          <span class="name">${cat.category}</span>
          <span class="qty">${cat.quantity}</span>
        </div>
      `).join('') || ''}
    </div>

    <!-- Product Breakdown -->
    ${printData.summary?.products?.length > 0 ? `
      <h2>Product Breakdown</h2>
      <div class="category-grid">
        ${printData.summary.products.map(product => `
          <div class="category-box">
            <span class="name">
              ${product.product_name}
              ${product.is_one_off ? '<span class="one-off-tag">one-off</span>' : ''}
            </span>
            <span class="qty">${product.quantity}</span>
          </div>
        `).join('')}
      </div>
    ` : ''}

    <!-- Orders Checklist -->
    <h2>Order Checklists (${printData.orders_by_client?.length || 0})</h2>
    ${buildOrdersHTML(printData)}

    <!-- Footer -->
    <div class="footer">
      Generated on ${new Date().toLocaleString()} • MAD Donuts Production System
    </div>
  </body>
  </html>
`;
